import { useQuery } from "@apollo/client";
import {
  ChartBarIcon,
  ClipboardDocumentListIcon,
} from "@heroicons/react/24/outline";
import {
  Button,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import EmptyState from "../../components/EmptyState/EmptyState";
import { ErrorAlert } from "../../components/ErrorAlert/ErrorAlert";
import Page from "../../components/Page/Page";
import Panel from "../../components/Panel/Panel";
import Badge from "../../components/Styled/Badge";
import { SurveyListData } from "../../graphql/graphql";
import { SURVEYS } from "../../graphql/queries-gql";

const SurveyTitle = styled.div`
  font-size: 13.5px;
  font-weight: 500;
  color: ${(props) => props.theme.textColor};
`;

const SurveyId = styled.div`
  margin-top: 2px;
  font-size: 11.5px;
  font-family: ${(props) => props.theme.monoFontFamily};
  color: ${(props) => props.theme.mutedTextColor};
`;

const Count = styled.span`
  font-size: 14px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${(props) => props.theme.textColor};
`;

const ButtonIcon = styled(ChartBarIcon)`
  width: 16px;
  height: 16px;
`;

const SurveysList = () => {
  const { data, loading, error } = useQuery<{ surveys: SurveyListData[] }>(SURVEYS);
  const navigate = useNavigate();

  if (loading) {
    return (
      <Page title="Surveys" maxWidth={1100}>
        <Skeleton variant="rounded" height={280} />
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Surveys" maxWidth={1100}>
        <ErrorAlert error={`Error loading surveys: ${error.message}`} />
      </Page>
    );
  }

  const surveys = data?.surveys ?? [];

  return (
    <Page
      title="Surveys"
      badge={surveys.length > 0 ? <Badge>{surveys.length}</Badge> : undefined}
      description="Pick a survey to see how users answered."
      maxWidth={1100}
    >
      {surveys.length === 0 ? (
        <EmptyState Icon={ClipboardDocumentListIcon} title="No surveys found" />
      ) : (
        <Panel padding={0}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Survey title</TableCell>
                  <TableCell align="center">Total responses</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {surveys.map((survey) => (
                  <TableRow key={survey._id} hover>
                    <TableCell sx={{ py: 1.25 }}>
                      <SurveyTitle>{survey.title}</SurveyTitle>
                      <SurveyId>ID: {survey._id}</SurveyId>
                    </TableCell>
                    <TableCell align="center">
                      <Count>{survey.totalAnswers}</Count>
                    </TableCell>
                    <TableCell align="center">
                      <Badge $tone={survey.totalAnswers > 0 ? "green" : "neutral"}>
                        {survey.totalAnswers > 0 ? "Active" : "No responses"}
                      </Badge>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        color="inherit"
                        size="small"
                        startIcon={<ButtonIcon />}
                        onClick={() => navigate(`/surveys/${survey._id}/analytics`)}
                        disabled={survey.totalAnswers === 0}
                      >
                        View analytics
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Panel>
      )}
    </Page>
  );
};

export default SurveysList;
